// Changing one number of a league's scoring (PLAN-2026-10-18 §2.3): the
// sheet a row of the editing table opens, and the one that adds a big-game
// bonus. Each change is applied as it is made, through the domain's
// `editScoring`, so the table behind the sheet shows it at once — a peek
// sheet on a phone, the table still in view beside it wider.
//
// WHO A NUMBER IS FOR: every position, unless the commissioner says only this
// one (how a tight end's catch gets its own number). Flipping that choice
// changes nothing by itself; the next number typed goes where it says.

import { useState } from 'react';
import {
  SCORING_LIMITS,
  editScoring,
  type ScoringConfig,
  type ScoringEdit,
  type ScoringGroup,
  type ScoringRow,
  type ScoringSection,
  type ScoringTable,
} from '@fsp/domain';
import type { SportDefinition } from '@fsp/sports-core';
import { Button, Choice, NumberField, OneOf, Stack, Text } from '../../kit/ui';
import { Sheet } from '../../kit/overlays';
import { pointsText } from './ScoringTableView';

export type ScoringTarget =
  | { kind: 'row'; row: ScoringRow; section: ScoringSection }
  | { kind: 'add-bonus'; section: ScoringSection; group: ScoringGroup }
  | null;

/** The kinds of play that take a big-game bonus: those with yards in them. */
export function bonusGroups(sport: SportDefinition): (groupId: string) => boolean {
  const yards = new Set(sport.stats.filter((s) => s.kind === 'yards').map((s) => s.key));
  const ids = new Set(
    (sport.scoringLayout?.groups ?? [])
      .filter((g) => g.stats.some((k) => yards.has(k)))
      .map((g) => g.id),
  );
  return (id) => ids.has(id);
}

export function ScoringEditor({
  target,
  onClose,
  sport,
  scoring,
  table,
  onChange,
}: {
  target: ScoringTarget;
  onClose: () => void;
  sport: SportDefinition;
  scoring: ScoringConfig;
  table: ScoringTable;
  onChange: (next: ScoringConfig) => void;
}) {
  const apply = (edit: ScoringEdit) => onChange(editScoring(scoring, edit, sport));
  return (
    <Sheet
      opened={target !== null}
      onClose={onClose}
      peek
      title={
        target === null
          ? undefined
          : target.kind === 'row'
            ? `${target.section.title}: ${target.row.label}`
            : `${target.section.title}: a big-game bonus`
      }
    >
      {target?.kind === 'row' ? (
        // Keyed by the row, so another row's sheet starts from its own numbers.
        <RowForm
          key={`${target.section.position}|${target.row.id}`}
          row={target.row}
          section={target.section}
          sport={sport}
          table={table}
          apply={apply}
          onClose={onClose}
        />
      ) : target?.kind === 'add-bonus' ? (
        <BonusForm
          key={`${target.section.position}|${target.group.id}`}
          group={target.group}
          sport={sport}
          apply={apply}
          onClose={onClose}
        />
      ) : null}
    </Sheet>
  );
}

function RowForm({
  row,
  section,
  sport,
  table,
  apply,
  onClose,
}: {
  row: ScoringRow;
  section: ScoringSection;
  sport: SportDefinition;
  table: ScoringTable;
  apply: (edit: ScoringEdit) => void;
  onClose: () => void;
}) {
  const stat = row.stats[0]!;
  const words = sport.stats.find((s) => s.key === stat)?.scoring;
  const [points, setPoints] = useState(row.points);
  const [unit, setUnit] = useState(row.unit);
  const [threshold, setThreshold] = useState(row.threshold ?? 0);
  // The rule a bonus edit names, which moves when its threshold does.
  const [ruleThreshold, setRuleThreshold] = useState(row.threshold ?? 0);
  // Every position this stat shows for, so "only" is offered when it means something.
  const sharedWith = table.sections.filter((s) =>
    [...s.groups, ...s.more].some((g) => g.rows.some((r) => r.id === row.id)),
  );
  const [who, setWho] = useState<'all' | 'one'>(row.own ? 'one' : 'all');
  const position = who === 'one' ? section.position : null;
  // Others with a number of their own, said so the commissioner knows a
  // change for everyone leaves them as they are.
  const keepOwn = sharedWith.filter(
    (s) =>
      s.position !== section.position &&
      [...s.groups, ...s.more].some((g) => g.rows.some((r) => r.id === row.id && r.own)),
  );

  if (row.kind === 'band') {
    const index = Number(row.id.split(':')[3]);
    return (
      <Stack gap={3}>
        <NumberField
          label="Points"
          value={points}
          onChange={(v) => {
            const next = v ?? 0;
            setPoints(next);
            apply({
              kind: 'band',
              stat,
              index,
              points: next,
              scope: row.scope ?? 'game',
              position: section.position,
            });
          }}
          min={-SCORING_LIMITS.bandPoints}
          max={SCORING_LIMITS.bandPoints}
          step={0.5}
          decimals={2}
          negative
        />
        {row.usual !== null ? (
          <Text size="xs" tone="secondary" block>
            Usually {pointsText(row.usual)}.
          </Text>
        ) : null}
        <Button onClick={onClose}>Done</Button>
      </Stack>
    );
  }

  if (row.kind === 'bonus') {
    return (
      <Stack gap={3}>
        <NumberField
          label={`At least this many ${words?.plural ?? 'of it'} in a game`}
          value={threshold}
          onChange={(v) => {
            const next = Math.max(SCORING_LIMITS.threshold.min, v ?? 0);
            setThreshold(next);
            apply({
              kind: 'bonus',
              stat,
              threshold: ruleThreshold,
              scope: row.scope ?? 'game',
              position: row.own ? section.position : null,
              points,
              newThreshold: next,
            });
            setRuleThreshold(next);
          }}
          min={SCORING_LIMITS.threshold.min}
          max={SCORING_LIMITS.threshold.max}
        />
        <NumberField
          label="Bonus points"
          value={points}
          onChange={(v) => {
            const next = v ?? 0;
            setPoints(next);
            if (next !== 0)
              apply({
                kind: 'bonus',
                stat,
                threshold: ruleThreshold,
                scope: row.scope ?? 'game',
                position: row.own ? section.position : null,
                points: next,
              });
          }}
          min={-SCORING_LIMITS.bonusPoints}
          max={SCORING_LIMITS.bonusPoints}
          step={0.5}
          decimals={2}
          negative
        />
        <Stack gap={2}>
          <Button onClick={onClose}>Done</Button>
          <Button
            variant="quiet"
            icon="delete"
            onClick={() => {
              apply({
                kind: 'bonus-remove',
                stat,
                threshold: ruleThreshold,
                scope: row.scope ?? 'game',
              });
              onClose();
            }}
          >
            Remove this bonus
          </Button>
        </Stack>
      </Stack>
    );
  }

  const units = row.units.length > 1 ? row.units : null;
  return (
    <Stack gap={3}>
      <NumberField
        label={
          unit === 1 ? 'Points for each' : `Points every ${unit} ${words?.plural ?? ''}`.trim()
        }
        value={points}
        onChange={(v) => {
          const next = v ?? 0;
          setPoints(next);
          apply({ kind: 'unit', stat, position, points: next, unit });
        }}
        min={-SCORING_LIMITS.points}
        max={SCORING_LIMITS.points}
        step={0.5}
        decimals={2}
        negative
      />
      {units ? (
        <Choice
          label="Counted"
          searchable={false}
          required
          value={String(unit)}
          options={units.map((u) => ({
            value: String(u),
            label: u === 1 ? 'Each one' : `Every ${u} ${words?.plural ?? ''}`.trim(),
          }))}
          onChange={(v) => {
            if (!v) return;
            const next = Number(v);
            setUnit(next);
            apply({ kind: 'unit', stat, position, points, unit: next });
          }}
        />
      ) : null}
      {sharedWith.length > 1 ? (
        <OneOf
          label="Who it’s for"
          value={who}
          onChange={setWho}
          options={[
            { value: 'all', label: 'Every position' },
            { value: 'one', label: `Only ${section.title.toLowerCase()}` },
          ]}
        />
      ) : null}
      {row.usual !== null ? (
        <Text size="xs" tone="secondary" block>
          Usually {pointsText(row.usual)}
          {row.unit === 1 ? '' : ` every ${row.unit} ${words?.plural ?? ''}`.trimEnd()}.
        </Text>
      ) : null}
      {who === 'all' && keepOwn.length > 0 ? (
        <Text size="xs" tone="secondary" block>
          {keepOwn.map((s) => s.title).join(', ')} keep a number of their own for this.
        </Text>
      ) : null}
      <Stack gap={2}>
        <Button onClick={onClose}>Done</Button>
        {row.own ? (
          <Button
            variant="quiet"
            onClick={() => {
              apply({ kind: 'own-clear', stat, position: section.position });
              onClose();
            }}
          >
            Give {section.title.toLowerCase()} everyone’s number
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}

function BonusForm({
  group,
  sport,
  apply,
  onClose,
}: {
  group: ScoringGroup;
  sport: SportDefinition;
  apply: (edit: ScoringEdit) => void;
  onClose: () => void;
}) {
  const options = (sport.scoringLayout?.groups.find((g) => g.id === group.id)?.stats ?? [])
    .map((k) => sport.stats.find((s) => s.key === k))
    .filter((s) => s !== undefined && s.kind === 'yards' && s.scoring !== undefined)
    .map((s) => ({ value: s!.key, label: s!.scoring!.plural }));
  const [stat, setStat] = useState(options[0]?.value ?? '');
  const [threshold, setThreshold] = useState(100);
  const [points, setPoints] = useState(2);
  return (
    <Stack gap={3}>
      <Choice
        label="For"
        searchable={false}
        required
        value={stat}
        options={options}
        onChange={(v) => v && setStat(v)}
      />
      <NumberField
        label="At least this many in a game"
        value={threshold}
        onChange={(v) => setThreshold(v ?? SCORING_LIMITS.threshold.min)}
        min={SCORING_LIMITS.threshold.min}
        max={SCORING_LIMITS.threshold.max}
      />
      <NumberField
        label="Bonus points"
        value={points}
        onChange={(v) => setPoints(v ?? 0)}
        min={-SCORING_LIMITS.bonusPoints}
        max={SCORING_LIMITS.bonusPoints}
        step={0.5}
        decimals={2}
        negative
      />
      <Button
        disabled={!stat || points === 0}
        onClick={() => {
          apply({ kind: 'bonus-add', stat, threshold, points });
          onClose();
        }}
      >
        Add the bonus
      </Button>
    </Stack>
  );
}

/** Whole points or decimals, the editor's last row. */
export function ScoringDecimals({
  scoring,
  sport,
  onChange,
}: {
  scoring: ScoringConfig;
  sport: SportDefinition;
  onChange: (next: ScoringConfig) => void;
}) {
  return (
    <OneOf
      label="Scores keep"
      value={String(scoring.decimals) as '0' | '1' | '2'}
      onChange={(v) =>
        onChange(editScoring(scoring, { kind: 'decimals', decimals: Number(v) }, sport))
      }
      options={[
        { value: '0', label: 'Whole points' },
        { value: '1', label: 'One decimal' },
        { value: '2', label: 'Two decimals' },
      ]}
    />
  );
}
